import useActiveOrdersContext from '@/contexts/activeOrders/useActiveOrdersContext'
import { UseSeller } from '@/contexts/seller/types'
import { getScheduledOrdersLengthForSidebar } from '@/pages/ScheduledOrders/utils'
import { useCallback, useEffect, useMemo, useRef } from 'react'
import { SIDEBAR_MENU as dict } from '@/resources/dict'
import { SidebarMenuItem } from '@/resources/types'
import { SidebarMenuItems } from '@/resources/enums'

interface Props {
  useSeller: UseSeller
  menuItems: SidebarMenuItem[]
  setMenuItems: (items: SidebarMenuItem[]) => void
}

const useSidebarScheduledHandler = ({
  useSeller: { hasTwinSeller },
  menuItems,
  setMenuItems,
}: Props) => {
  const { activeOrders } = useActiveOrdersContext()
  const scheduledOrdersCount = useRef<number | undefined>(undefined)

  const scheduledOrdersLength = useMemo(
    () => getScheduledOrdersLengthForSidebar(activeOrders),
    [activeOrders]
  )

  const handleScheduledMenuItem = useCallback(
    (menuItem: SidebarMenuItem) => {
      if (menuItem.id === SidebarMenuItems.SCHEDULED) {
        const count = scheduledOrdersCount.current
        const countText = count === undefined ? '' : ` (${count})`
        menuItem.text = `${dict.scheduledOrders}${countText}`
        if (hasTwinSeller) menuItem.featureFlag = false
      }
    },
    [hasTwinSeller]
  )

  const updateMenuItem = useCallback(() => {
    const scheduledMenuItem = menuItems.find(
      item => item.id === SidebarMenuItems.SCHEDULED
    )
    if (scheduledMenuItem) {
      handleScheduledMenuItem(scheduledMenuItem)
      setMenuItems([...menuItems])
    }
  }, [handleScheduledMenuItem, menuItems, setMenuItems])

  useEffect(() => {
    if (
      menuItems.length &&
      scheduledOrdersLength !== scheduledOrdersCount.current
    ) {
      scheduledOrdersCount.current = scheduledOrdersLength
      updateMenuItem()
    }
  }, [scheduledOrdersLength, scheduledOrdersCount, menuItems, updateMenuItem])

  return {
    handleScheduledMenuItem,
  }
}

export default useSidebarScheduledHandler
